/**
 * Interactive Calculate button with pulse animation on press and loading spinner
 * Uses React Native Animated with useNativeDriver for UI-thread animations
 * (avoids frame drops during camera frame processing on JS thread)
 */

import React, { useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Animated,
} from 'react-native';

const ACCENT_CYAN = '#00b8d9';
const ACCENT_CYAN_DIM = '#0097a7';

interface AnimatedCalculateButtonProps {
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** Skip animation when camera/live monitoring active to avoid frame drops */
  reduceMotion?: boolean;
}

export function AnimatedCalculateButton({
  onPress,
  loading = false,
  disabled = false,
  reduceMotion = false,
}: AnimatedCalculateButtonProps) {
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    if (!disabled && !loading && !reduceMotion) {
      Animated.timing(scaleAnim, {
        toValue: 0.92,
        duration: 80,
        useNativeDriver: true,
      }).start();
    }
  };

  const handlePressOut = () => {
    if (reduceMotion) {
      scaleAnim.setValue(1);
      return;
    }
    Animated.spring(scaleAnim, {
      toValue: 1,
      damping: 15,
      stiffness: 300,
      useNativeDriver: true,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      disabled={disabled || loading}
      accessibilityRole="button"
      accessibilityLabel={loading ? 'Calculating dose' : 'Calculate flight dose'}
      accessibilityState={{ disabled: disabled || loading, busy: loading }}
    >
      <Animated.View
        style={[
          styles.button,
          !loading && styles.primaryButtonLight,
          (disabled || loading) && styles.disabled,
          { transform: [{ scale: scaleAnim }] },
        ]}
      >
        {loading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color="#ffffff" />
            <Text style={styles.buttonText}>Calculating…</Text>
          </View>
        ) : (
          <Text style={styles.buttonText}>Calculate</Text>
        )}
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    backgroundColor: ACCENT_CYAN,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 4,
  },
  primaryButtonLight: {
    backgroundColor: ACCENT_CYAN_DIM,
  },
  disabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#ffffff',
    fontWeight: '700',
    fontSize: 16,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
});
